class AlgoliaInstantSearch extends HTMLElement {
  constructor() {
    super();

    this.afterISStartRemoveDefaultWidget = this.afterISStartRemoveDefaultWidget.bind(this);
    this.beforeInstantSearchAllowParamsArray = this.beforeInstantSearchAllowParamsArray.bind(this);
    this.beforeInstantSearchMainTemplate = this.beforeInstantSearchMainTemplate.bind(this);
    this.beforeInstantSearchOptions = this.beforeInstantSearchOptions.bind(this);
    this.beforeISStartAddWidgetArray = this.beforeISStartAddWidgetArray.bind(this);
    this.connectedCallback = this.connectedCallback.bind(this);
    this.onMount = this.onMount.bind(this);
    this.setupWidget = this.setupWidget.bind(this);
    this.setupWidgets  = this.setupWidgets.bind(this);
    this.subscribe = this.subscribe.bind(this);
  }

  /**
   * Removes default widgets after instant search has started.
   * @param {Array} defaultWidgets - The array of default widgets to filter.
   * @returns {Array} An array of widgets with specific types filtered out.
   */
  afterISStartRemoveDefaultWidget(defaultWidgets) {
    const widgetTypes = ['ais.hits', 'ais.pagination', 'ais.sortBy'];
    const filteredWidgets = defaultWidgets.filter(widget => widgetTypes.includes(widget.$$widgetType));
    return filteredWidgets;
  }

  /**
   * Concatenates query params to the given allowed params.
   * If allowParams is an array, it adds query params to it. If it is a single value, it creates a new array with the value and then adds query params.
   * @param {(Array|string)} allowParams - The array or value to which query params will be added.
   * @returns {Array} A new array containing the allowd params.
   */
  beforeInstantSearchAllowParamsArray(allowParams) {
    try {
      const href = window.location.href;
      const url = new URL(href);
      const search = new URLSearchParams(url.search);
      const params = Array.from(search.keys());
      return params;
    } catch (e) {
      console.error('Error', e);
      const defaultParams = [].concat(
        allowParams,
        "icid",
        "tid",
        "utm_campaign",
        "utm_content",
        "utm_medium",
        "utm_source",
        "utm_term",
      );
      return defaultParams;
    }
  }
    
  /**
   * Generates the main template for the instant search before the default template.
   * Delegates to a layout-specific template method based on the filter layout setting.
   * @param {Object} defaultTemplate - The default template.
   * @param {Object} data - Data used for generating the template.
   * @param {Function} html - Function for generating HTML elements.
   * @returns {HTMLElement} - The generated HTML template.
   */
  beforeInstantSearchMainTemplate(_defaultTemplate, data, html) {
    switch (this.settings.filterLayout) {
      case 'sidebar_collapsible':
        return this.templateSidebarCollapsible(data, html);
      case 'drawer':
        return this.templateDrawer(data, html);
      case 'horizontal':
        return this.templateHorizontal(data, html);
      case 'sidebar_visible':
      default:
        return this.templateSidebarVisible(data, html);
    }

  }


  /**
   * Layout: Sidebar – Always Visible (default).
   * Filters displayed in a permanent left sidebar alongside the product grid.
   */
  templateSidebarVisible(data, html) {
    return html`
      <div class="template-container">
        <div class="ais-section-padding viewport-spacing template-main-collection">
          <div class="ais-input hidden">
            <div class="ais-input-button"></div>
            <div class="ais-clear-input-icon"></div>
            <div class="ais-search-box-container"></div>
          </div>
          <div class="ais-clear-refinements-container hidden"></div>
          <div class="ais-search-header hidden">
            <div class="ais-change-display">
              <span class="ais-change-display-block ais-change-display-selected"></span>
              <span class="ais-change-display-list"></span>
            </div>
          </div>
          <div class="ais-facets-button hidden"></div>
          <div class="ais-hits-container hidden"></div>
          <div class="ais-pagination-container hidden"></div>
          ${data.facets.map(facet => html`<div class="ais-facet-${facet.escapedName}-container hidden"></div>`)}
          ${this.statsDetails.mobile.renderContainer(html)}
          <div class="facets facets-vertical-sort small-hide medium-hide no-js-hidden">
            <form class="facets-vertical-form">
              <div class="facets-vertical-form-holder">
                ${this.statsDetails.desktop.renderContainer(html)}
                ${this.sortBy.desktop.renderContainer(html)}
              </div>
              ${this.stats.desktop.renderContainer(html)}
            </form>
          </div>
          <div class="facets-vertical">
            <aside class="facets-wrapper" id="main-collection-filters">
              <div class="facets-container">
                <facet-filters-form class="facets facets small-hide medium-hide">
                  <form class="facets__form-vertical">
                    <div id="FacetsWrapperDesktop">
                      <div class="active-facets active-facets-desktop">
                        ${this.clearRefinements.desktop.renderContainer(html)}
                        ${this.currentRefinements.desktop.renderContainer(html)}
                      </div>
                    </div>
                    ${this.refinementList.desktop.map(desktop => desktop.renderContainer(html))}
                  </form>
                </facet-filters-form>
                ${this.stats.mobile.renderContainer(html)}
                ${this.showCurrentRefinements.mobile.renderContainer(html)}
              </div>
            </aside>
            <div class="ais-facets facets-container large-up-hide mobile-facets__main">
              ${this.sortBy.mobile.renderContainer(html)}
              ${this.clearRefinements.mobile.renderContainer(html)}
              ${this.currentRefinements.mobile.renderContainer(html)}
              ${this.refinementList.mobile.map(mobile => mobile.renderContainer(html))}
            </div>
            ${this.infiniteHits.desktop.renderContainer(html)}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Layout: Sidebar – Toggleable.
   * Same sidebar layout but with a toggle button to show/hide filters on desktop.
   * Inspired by the Eileen Fisher mainline collection page.
   */
  templateSidebarCollapsible(data, html) {
    return html`
      <div class="template-container">
        <div class="ais-section-padding viewport-spacing template-main-collection filter-layout--sidebar-collapsible">
          <div class="ais-input hidden">
            <div class="ais-input-button"></div>
            <div class="ais-clear-input-icon"></div>
            <div class="ais-search-box-container"></div>
          </div>
          <div class="ais-clear-refinements-container hidden"></div>
          <div class="ais-search-header hidden">
            <div class="ais-change-display">
              <span class="ais-change-display-block ais-change-display-selected"></span>
              <span class="ais-change-display-list"></span>
            </div>
          </div>
          <div class="ais-facets-button hidden"></div>
          <div class="ais-hits-container hidden"></div>
          <div class="ais-pagination-container hidden"></div>
          ${data.facets.map(facet => html`<div class="ais-facet-${facet.escapedName}-container hidden"></div>`)}
          ${this.statsDetails.mobile.renderContainer(html)}
          <div class="facets facets-vertical-sort small-hide medium-hide no-js-hidden">
            <form class="facets-vertical-form">
              <div class="facets-vertical-form-holder">
                ${this.statsDetails.desktop.renderContainer(html)}
                <button type="button" class="filter-toggle-btn no-js-hidden" onClick=${(e) => {
                  e.preventDefault();
                  const layout = e.target.closest('.filter-layout--sidebar-collapsible');
                  if (layout) layout.classList.toggle('filters-hidden');
                }}>
                <div class="filter-toggle-circle">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
                </div>
                  <span class="filter-toggle-btn__hide">Hide Filter</span>
                  <span class="filter-toggle-btn__show">Show Filter</span>
                </button>
                ${this.sortBy.desktop.renderContainer(html)}
              </div>
              ${this.stats.desktop.renderContainer(html)}
            </form>
          </div>
          <div class="facets-vertical">
            <aside class="facets-wrapper" id="main-collection-filters">
              <div class="facets-container">
                <facet-filters-form class="facets facets small-hide medium-hide">
                  <form class="facets__form-vertical">
                    <div id="FacetsWrapperDesktop">
                      <div class="active-facets active-facets-desktop">
                        ${this.clearRefinements.desktop.renderContainer(html)}
                        ${this.currentRefinements.desktop.renderContainer(html)}
                      </div>
                    </div>
                    ${this.refinementList.desktop.map(desktop => desktop.renderContainer(html))}
                  </form>
                </facet-filters-form>
                ${this.stats.mobile.renderContainer(html)}
                ${this.showCurrentRefinements.mobile.renderContainer(html)}
              </div>
            </aside>
            <div class="ais-facets facets-container large-up-hide mobile-facets__main">
              ${this.sortBy.mobile.renderContainer(html)}
              ${this.clearRefinements.mobile.renderContainer(html)}
              ${this.currentRefinements.mobile.renderContainer(html)}
              ${this.refinementList.mobile.map(mobile => mobile.renderContainer(html))}
            </div>
            ${this.infiniteHits.desktop.renderContainer(html)}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Layout: Drawer Overlay.
   * Full-width product grid with a "Filter & Sort" button that opens a slide-out
   * drawer panel from the left. Inspired by the Brooks Running collection page.
   */
  templateDrawer(data, html) {
    const openDrawer = () => {
      const overlay = document.querySelector('.filter-drawer-overlay');
      if (overlay) {
        overlay.classList.add('is-open');
        document.body.classList.add('filter-drawer-open');
      }
    };
    const closeDrawer = () => {
      const overlay = document.querySelector('.filter-drawer-overlay');
      if (overlay) {
        overlay.classList.remove('is-open');
        document.body.classList.remove('filter-drawer-open');
      }
    };

    return html`
      <div class="template-container">
        <div class="ais-section-padding viewport-spacing template-main-collection filter-layout--drawer">
          <div class="ais-input hidden">
            <div class="ais-input-button"></div>
            <div class="ais-clear-input-icon"></div>
            <div class="ais-search-box-container"></div>
          </div>
          <div class="ais-clear-refinements-container hidden"></div>
          <div class="ais-search-header hidden">
            <div class="ais-change-display">
              <span class="ais-change-display-block ais-change-display-selected"></span>
              <span class="ais-change-display-list"></span>
            </div>
          </div>
          <div class="ais-facets-button hidden"></div>
          <div class="ais-hits-container hidden"></div>
          <div class="ais-pagination-container hidden"></div>
          ${data.facets.map(facet => html`<div class="ais-facet-${facet.escapedName}-container hidden"></div>`)}
          ${this.statsDetails.mobile.renderContainer(html)}
          <div class="facets facets-vertical-sort small-hide medium-hide no-js-hidden">
            <form class="facets-vertical-form">
              <div class="drawer-toolbar-left">
                <button type="button" class="filter-drawer-toggle-btn no-js-hidden" onClick=${openDrawer}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
                  <span>Filter & Sort</span>
                </button>
                ${this.statsDetails.desktop.renderContainer(html)}
              </div>
              ${this.stats.desktop.renderContainer(html)}
            </form>
          </div>
          <div class="filter-drawer-overlay">
            <div class="filter-drawer-backdrop" onClick=${closeDrawer}></div>
            <div class="filter-drawer">
              <div class="filter-drawer__header">
                <span class="filter-drawer__title p-regular p-bold">Filter & Sort</span>
                <button type="button" class="filter-drawer__close" onClick=${closeDrawer}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div class="filter-drawer__body">
                <div class="filter-drawer__sort">
                  ${this.sortBy.desktop.renderContainer(html)}
                </div>
                <div class="active-facets active-facets-desktop">
                  ${this.clearRefinements.desktop.renderContainer(html)}
                  ${this.currentRefinements.desktop.renderContainer(html)}
                </div>
                ${this.refinementList.desktop.map(desktop => desktop.renderContainer(html))}
              </div>
            </div>
          </div>
          <div class="facets-vertical">
            <div class="facets-container large-up-hide">
              ${this.stats.mobile.renderContainer(html)}
              ${this.showCurrentRefinements.mobile.renderContainer(html)}
            </div>
            <div class="ais-facets facets-container large-up-hide mobile-facets__main">
              ${this.sortBy.mobile.renderContainer(html)}
              ${this.clearRefinements.mobile.renderContainer(html)}
              ${this.currentRefinements.mobile.renderContainer(html)}
              ${this.refinementList.mobile.map(mobile => mobile.renderContainer(html))}
            </div>
            ${this.infiniteHits.desktop.renderContainer(html)}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Layout: Horizontal Filter Bar.
   * Filters displayed as dropdown menus in a horizontal bar above the full-width
   * product grid. Inspired by the Allbirds collection page.
   */
  templateHorizontal(data, html) {
    return html`
      <div class="template-container">
        <div class="ais-section-padding viewport-spacing template-main-collection filter-layout--horizontal">
          <div class="ais-input hidden">
            <div class="ais-input-button"></div>
            <div class="ais-clear-input-icon"></div>
            <div class="ais-search-box-container"></div>
          </div>
          <div class="ais-clear-refinements-container hidden"></div>
          <div class="ais-search-header hidden">
            <div class="ais-change-display">
              <span class="ais-change-display-block ais-change-display-selected"></span>
              <span class="ais-change-display-list"></span>
            </div>
          </div>
          <div class="ais-facets-button hidden"></div>
          <div class="ais-hits-container hidden"></div>
          <div class="ais-pagination-container hidden"></div>
          ${data.facets.map(facet => html`<div class="ais-facet-${facet.escapedName}-container hidden"></div>`)}
          ${this.statsDetails.mobile.renderContainer(html)}
          ${this.statsDetails.desktop.renderContainer(html)}
          <div class="facets filter-bar small-hide medium-hide">
            <div class="filter-bar__filters">
              ${this.refinementList.desktop.map(desktop => desktop.renderContainer(html))}
            </div>
            <div class="filter-bar__actions">
              ${this.sortBy.desktop.renderContainer(html)}
              ${this.stats.desktop.renderContainer(html)}
            </div>
          </div>
          <div class="filter-bar__active-facets small-hide medium-hide">
            <div class="active-facets active-facets-desktop">
              ${this.clearRefinements.desktop.renderContainer(html)}
              ${this.currentRefinements.desktop.renderContainer(html)}
            </div>
          </div>
          <div class="facets-vertical">
            <div class="facets-container large-up-hide">
              ${this.stats.mobile.renderContainer(html)}
              ${this.showCurrentRefinements.mobile.renderContainer(html)}
            </div>
            <div class="ais-facets facets-container large-up-hide mobile-facets__main">
              ${this.sortBy.mobile.renderContainer(html)}
              ${this.clearRefinements.mobile.renderContainer(html)}
              ${this.currentRefinements.mobile.renderContainer(html)}
              ${this.refinementList.mobile.map(mobile => mobile.renderContainer(html))}
            </div>
            ${this.infiniteHits.desktop.renderContainer(html)}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Modifies the instant search options before initialization.
   * @param {Object} options - instant search options object.
   * @returns {Object} Modified instant search options object.
   */
  beforeInstantSearchOptions(options) {
    options.hitsPerPage = this.settings.productsPerPage;
    return options;
  }

  /**
   * Creates and returns an array of widgets to be added before instant search starts.
   * @returns {Array} Array of widgets.
   */
  beforeISStartAddWidgetArray() {
    const clearRefinementsDesktop = this.clearRefinements.desktop.createWidget();
    const clearRefinementsMobile = this.clearRefinements.mobile.createWidget();
    const currentRefinementsDesktop = this.currentRefinements.desktop.createWidget();
    const currentRefinementsMobile = this.currentRefinements.mobile.createWidget();
    const infiniteHitsDesktop = this.infiniteHits.desktop.createWidget();
    const showCurrentRefinementsMobile = this.showCurrentRefinements.mobile.createWidget();
    const sortByDesktop = this.sortBy.desktop.createWidget();
    const sortByMobile = this.sortBy.mobile.createWidget();
    const statsDesktop = this.stats.desktop.createWidget();
    const statsMobile = this.stats.mobile.createWidget();
    const statsDetailsDesktop = this.statsDetails.desktop.createWidget();
    const statsDetailsMobile = this.statsDetails.mobile.createWidget();
    const refinementListsDesktop = this.refinementList.desktop.map(desktop => desktop.createWidget());
    const refinementListsMobile = this.refinementList.mobile.map(mobile => mobile.createWidget());

    return [
      clearRefinementsDesktop,
      clearRefinementsMobile,
      currentRefinementsDesktop,
      currentRefinementsMobile,
      infiniteHitsDesktop,
      showCurrentRefinementsMobile,
      sortByDesktop,
      sortByMobile,
      statsDesktop,
      statsMobile,
      statsDetailsDesktop,
      statsDetailsMobile,
      ...refinementListsDesktop,
      ...refinementListsMobile,
    ];
  }

  /**
   * Initializes settings based on custom attributes when the element is connected to the DOM.
   * @returns {void}
   */
  connectedCallback() {
    const rawPropertiesToGroup = this.getAttribute('properties-to-group') || '';
    const propertiesToGroup = rawPropertiesToGroup
      .split(',')
      .map(prop => prop.trim())
      .filter(prop => prop.length > 0);

    this.settings = {
      productsPerPage: this.getAttribute('products-per-page'),
      enableGroupingFilter: this.getAttribute('enable-grouping-filter') === 'true',
      propertiesToGroup,
      filterLayout: this.getAttribute('filter-layout') || 'sidebar_visible',
      filterType: this.getAttribute('filter-type')
    };
    this.onMount();
  }

  /**
   * Registers an event listener for the 'algolia.hooks.initialize' event when the component mounts.
   * @returns {void}
   */
  onMount() {
    document.addEventListener('algolia.hooks.initialize', this.subscribe);
  }

  /**
   * Sets up a widget using the provided container, mobile flag, selector, and additional configuration.
   * @param {HTMLElement} container - The container element where the widget will be placed.
   * @param {boolean} isMobile - Indicates if the widget is being rendered on a mobile device.
   * @param {string} selector - CSS selector to find the widget element within the container.
   * @param {object} rest - Additional configuration options for the widget.
   * @returns {object|null} Object with methods to create and render the widget or null if the selector does not match any element.
   */
  setupWidget(container, isMobile, selector, rest) {
    const configuration = { container, isMobile, ...rest };
    const instances = this.instances;

    const element = this.querySelector(selector);
    if (!element) return null;

    return ({
      createWidget: () => element.createWidget(configuration, instances),
      renderContainer: (html) => element.renderContainer(configuration, html),
    });
  }

  /**
   * Initializes and configures all algolia widgets used in the application.
   * @returns {void}
   */
  setupWidgets() {
    this.clearRefinements = {
      desktop: this.setupWidget('ais-clear-current-refinements-container', false, 'algolia-clear-refinements'),
      mobile: this.setupWidget('ais-clear-current-refinements-container-mobile', true, 'algolia-clear-refinements')
    };

    this.currentRefinements = {
      desktop: this.setupWidget('ais-current-refined-values-container', false, 'algolia-current-refinements'),
      mobile: this.setupWidget('ais-current-refined-values-container-mobile', true, 'algolia-current-refinements')
    };

    let infiniteHitsConfig = {};

    if (this.settings.enableGroupingFilter) {
      infiniteHitsConfig = {
        transformItems: buildTransformItems(this.settings.propertiesToGroup)
      };
    }

    this.infiniteHits = {
      desktop: this.setupWidget(
        'ais-infinite-hits-container',
        false,
        'algolia-infinite-hits',
        infiniteHitsConfig,
      )
    };

    this.showCurrentRefinements = {
      mobile: this.setupWidget('ais-show-current-refined-values-container-mobile', true, 'algolia-show-current-refinements')
    };

    this.sortBy = {
      desktop: this.setupWidget('ais-sort-orders-container', false, 'algolia-sort-by'),
      mobile: this.setupWidget('ais-sort-orders-container-mobile', true, 'algolia-sort-by')
    };

    this.stats = {
      desktop: this.setupWidget('ais-stats-container', false, 'algolia-stats'),
      mobile: this.setupWidget('ais-stats-container-mobile', true, 'algolia-stats')
    };

    this.statsDetails = {
      desktop: this.setupWidget('ais-details-container', false, 'algolia-stats-details'),
      mobile: this.setupWidget('ais-details-container-mobile', true, 'algolia-stats-details')
    };

    /* When we are working with a specific collection, we want to use that collection's facets rather than the default, which is general. */
    const currentCollectionId = this.instances.algoliaShopify.current_collection_id;
    const collectionFacets = this.instances.algoliaShopify.config.collection_facets;
    let currentCollectionFacets;
    if (currentCollectionId) {
      currentCollectionFacets = collectionFacets[currentCollectionId] || collectionFacets["default"];
    } else {
      currentCollectionFacets = this.instances.algoliaShopify.config.facets;
    }
    let filteredCollectionFacets = [];
    if (currentCollectionFacets)  {
      filteredCollectionFacets = currentCollectionFacets.filter(collectionFacet => collectionFacet.type !== 'hidden');
    }

    this.refinementList = {
      desktop: filteredCollectionFacets.reduce((previous, current) => {
        const selector = current.type === 'slider' ? 'algolia-range-input' : 'algolia-refinement-list';
        const widget = this.setupWidget(current.name, false, selector, current);
        return current.enabled ? [...previous, widget] : previous;
      }, []),
      mobile: filteredCollectionFacets.reduce((previous, current) => {
        const selector = current.type === 'slider' ? 'algolia-range-input' : 'algolia-refinement-list';
        const widget = this.setupWidget(current.name, true, selector, current);
        return current.enabled ? [...previous, widget] : previous;
      }, [])
    };
  }

  /**
   * Initializes layout-specific behaviors after the template and widgets have rendered.
   * Called once after Algolia InstantSearch has fully initialized.
   * @returns {void}
   */
  initLayoutBehavior() {
    if (this.settings.filterLayout === 'horizontal') {
      this.initHorizontalBehavior();
    }

    if (this.settings.filterLayout === 'drawer') {
      this.initDrawerKeyboardBehavior();
    }


/* adding filter styling */
    const filterTypeStyle = document.querySelector('.facets');
     if (this.settings.filterType === 'style_1') {
      filterTypeStyle.classList.add('filter-style-1');
    }
    if (this.settings.filterType === 'style_2') {
      filterTypeStyle.classList.add('filter-style-2');
    }
    if (this.settings.filterType === 'style_3') {
      filterTypeStyle.classList.add('filter-style-3');
    }
  }

  /**
   * Initializes horizontal filter bar behavior:
   * - Waits for Algolia widgets to render content inside the dropdown details.
   * - Closes all desktop dropdowns on initial load (they render open by default).
   * - Moves show-more buttons into the dropdown panel on first toggle.
   * - Ensures only one dropdown is open at a time.
   * - Closes dropdowns when clicking outside.
   * - Reveals the filter bar (it starts at opacity 0 to prevent a flash of
   *   open dropdowns).
   * @returns {void}
   */
  initHorizontalBehavior() {
    const filterBar = document.querySelector('.filter-bar__filters');
    if (!filterBar) return;

    const desktopDetails = filterBar.querySelectorAll('.facets__disclosure-vertical');

    /* Wait for the Algolia widgets to render their summary/content before
       we close dropdowns and reveal the bar.  Until then the details are
       empty so there is nothing to flash. */
    const hasSummaries = Array.from(desktopDetails).some(d => d.querySelector('summary'));
    if (!hasSummaries && desktopDetails.length > 0) {
      requestAnimationFrame(() => this.initHorizontalBehavior());
      return;
    }

    /* Close all dropdowns initially — they render with open="true" by default. */
    desktopDetails.forEach(detail => detail.removeAttribute('open'));

    /* On toggle: move the show-more button into the dropdown div (on first
       open) so it sits below the facet list inside the absolutely-positioned
       dropdown panel.  Also enforce single-open behavior. */
   desktopDetails.forEach(detail => {
      detail.addEventListener('toggle', () => {
        if (detail.open) {
          const dropdownDiv = detail.querySelector(':scope > div');
          const showMoreBtn = detail.querySelector(':scope > .button-show-more');
          if (dropdownDiv && showMoreBtn && showMoreBtn.parentElement !== dropdownDiv) {
            dropdownDiv.appendChild(showMoreBtn);
          }
          desktopDetails.forEach(other => {
            if (other !== detail && other.open) other.removeAttribute('open');
          });
        }
      });
    });


    /* Close dropdowns when clicking outside the filter bar. */
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.filter-bar__filters')) {
        desktopDetails.forEach(detail => {
          if (detail.open) detail.removeAttribute('open');
        });
      }
    });


    /* Reveal the filter bar now that dropdowns are properly closed. */
    filterBar.classList.add('initialized');
  }

  /**
   * Initializes keyboard support for the drawer overlay (Escape to close).
   * @returns {void}
   */
  initDrawerKeyboardBehavior() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const overlay = document.querySelector('.filter-drawer-overlay.is-open');
        if (overlay) {
          overlay.classList.remove('is-open');
          document.body.classList.remove('filter-drawer-open');
        }
      }
    });
  }

  /**
   * Waits for the Algolia template to render, then initializes layout-specific behavior.
   * @returns {void}
   */
  initLayoutBehaviorWhenReady() {
    const check = () => {
      const container = document.querySelector('.template-main-collection');
      if (container) {
        this.initLayoutBehavior();
      } else {
        requestAnimationFrame(check);
      }
    };
    requestAnimationFrame(check);
  }

  /**
   * Subscribes to algolia hooks and sets up necessary configurations.
   * @returns {void}
   */
  subscribe() {
    this.instances = { algoliaShopify: window.algoliaShopify, instantSearch: window.instantsearch };
    if (!this.instances.algoliaShopify || !this.instances.instantSearch) return;

    this.setupWidgets();

    this.instances.algoliaShopify.hooks.registerHook('afterISStartRemoveDefaultWidget', this.afterISStartRemoveDefaultWidget);
    this.instances.algoliaShopify.hooks.registerHook('beforeInstantSearchAllowParamsArray', this.beforeInstantSearchAllowParamsArray);
    this.instances.algoliaShopify.hooks.registerHook('beforeInstantSearchMainTemplate', this.beforeInstantSearchMainTemplate);
    this.instances.algoliaShopify.hooks.registerHook('beforeInstantSearchOptions', this.beforeInstantSearchOptions);
    this.instances.algoliaShopify.hooks.registerHook('beforeISStartAddWidgetArray', this.beforeISStartAddWidgetArray);

    /* Initialize layout-specific interactive behaviors after Algolia renders. */
    if (this.settings.filterLayout !== 'sidebar_visible') {
      this.initLayoutBehaviorWhenReady();
    }
  };
}

const getValueByPath = (obj, path) => {
  try {
    return Function('obj', `return obj?.${path}`)(obj);
  } catch (e) {
    return undefined;
  }
}

/**
 * Returns a transformItems function that filters out duplicates from an item list
 * based on a set of property paths (e.g., 'title', 'sku').
 *
 * If no properties are provided, the original list is returned unmodified.
 * Otherwise, the function constructs a key for each item by combining the values
 * of the specified paths, and removes any items that share the same key.
 *
 * @param {string[]} properties - List of property paths used to build the deduplication key.
 * @returns {(items: object[]) => object[]} - A filtering function to deduplicate the item list.
 */
const buildTransformItems = (properties = []) => {
  if (!Array.isArray(properties) || properties.length === 0) {
    return items => items;
  }

  return (items) => {
    const seen = new Set();
    return items.filter(item => {
      const keyParts = properties.map(path => getValueByPath(item, path));
      if (keyParts.some(part => part === undefined)) return true;

      const key = keyParts.join('||').toLowerCase();
      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    });
  }
}

customElements.define("algolia-instant-search", AlgoliaInstantSearch);
